import { Document, Page, Text, View, Image, StyleSheet, Font, pdf } from '@react-pdf/renderer';
import { createElement } from 'react';
import type { ExportPayload, ExportEmployeeRow } from '../utils/export/exportTypes';
import { ALL_COLUMN_IDS, getColumnDefs } from '../utils/export/exportColumns';
import type { ExportColumnDef } from '../utils/export/exportColumns';

// Register Rubik variable font (Latin + Hebrew, all weights in one file)
// Served from public/fonts/ — sourced from google/fonts GitHub repo
Font.register({
  family: 'Rubik',
  fonts: [
    { src: '/fonts/Rubik-VariableFont.ttf', fontWeight: 400 },
    { src: '/fonts/Rubik-VariableFont.ttf', fontWeight: 600 },
    { src: '/fonts/Rubik-VariableFont.ttf', fontWeight: 700 },
  ],
});

// BrainsWay brand colors
const DEEP_BLUE = '#002C41';
const CYAN = '#1EB1CB';
const BLUE_GRAY = '#B1CCD4';

const ROW_ALT = '#f8fafc';
const BORDER_COLOR = '#e2e8f0';

const s = StyleSheet.create({
  page: {
    fontFamily: 'Rubik',
    fontSize: 9,
    padding: 30,
    paddingBottom: 50,
    color: '#334155',
  },
  // Logo header
  logoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  logo: {
    width: 120,
    height: 36,
    objectFit: 'contain',
  },
  logoSpacer: {
    flex: 1,
  },
  logoDate: {
    fontSize: 8,
    color: BLUE_GRAY,
  },
  // Brand divider
  brandDivider: {
    height: 3,
    backgroundColor: CYAN,
    marginBottom: 14,
  },
  // Header section
  header: {
    marginBottom: 14,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: DEEP_BLUE,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 11,
    color: '#64748b',
    marginBottom: 2,
  },
  // Totals bar
  totalsBar: {
    flexDirection: 'row',
    backgroundColor: DEEP_BLUE,
    borderRadius: 6,
    padding: 10,
    marginBottom: 16,
    gap: 20,
  },
  totalItem: {
    flex: 1,
  },
  totalLabel: {
    fontSize: 7,
    color: BLUE_GRAY,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  totalValue: {
    fontSize: 14,
    fontWeight: 700,
    color: '#ffffff',
  },
  // Therapist group
  groupHeader: {
    flexDirection: 'row',
    backgroundColor: DEEP_BLUE,
    borderRadius: 4,
    padding: 6,
    marginTop: 10,
    marginBottom: 2,
  },
  groupName: {
    fontSize: 10,
    fontWeight: 600,
    color: '#ffffff',
    flex: 1,
  },
  groupTotal: {
    fontSize: 10,
    fontWeight: 700,
    color: CYAN,
  },
  // Table header
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    borderBottomWidth: 1,
    borderBottomColor: BORDER_COLOR,
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  th: {
    fontSize: 7,
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
  },
  // Table row
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: BORDER_COLOR,
    paddingVertical: 3,
    paddingHorizontal: 4,
  },
  tableRowAlt: {
    backgroundColor: ROW_ALT,
  },
  td: {
    fontSize: 8,
    color: '#475569',
  },
  tdBold: {
    fontSize: 8,
    fontWeight: 600,
    color: DEEP_BLUE,
  },
  tdStrike: {
    fontSize: 8,
    color: '#94a3b8',
    textDecoration: 'line-through',
  },
  // Allowance row
  allowanceRow: {
    flexDirection: 'row',
    backgroundColor: '#e6f7fa',
    borderBottomWidth: 0.5,
    borderBottomColor: BLUE_GRAY,
    paddingVertical: 3,
    paddingHorizontal: 4,
  },
  allowanceText: {
    fontSize: 8,
    color: DEEP_BLUE,
    fontWeight: 600,
  },
  // Supervision bonus row
  supervisionRow: {
    flexDirection: 'row',
    backgroundColor: '#fef3c7',
    borderBottomWidth: 0.5,
    borderBottomColor: '#fbbf24',
    paddingVertical: 3,
    paddingHorizontal: 4,
  },
  supervisionText: {
    fontSize: 8,
    color: '#78350f',
    fontWeight: 600,
  },
  // Grand totals
  grandTotals: {
    flexDirection: 'row',
    backgroundColor: DEEP_BLUE,
    borderRadius: 4,
    padding: 8,
    marginTop: 12,
  },
  grandLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: '#ffffff',
    flex: 1,
  },
  grandValue: {
    fontSize: 12,
    fontWeight: 700,
    color: CYAN,
  },
  // Footer
  footer: {
    position: 'absolute',
    bottom: 14,
    left: 30,
    right: 30,
    borderTopWidth: 0.5,
    borderTopColor: BLUE_GRAY,
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 7,
    color: BLUE_GRAY,
  },
  footerPage: {
    fontSize: 7,
    color: BLUE_GRAY,
  },
  // Filter chips
  filtersRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 10,
  },
  filterChip: {
    backgroundColor: '#f1f5f9',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    fontSize: 7,
    color: '#64748b',
  },
});

function getColWidth(col: ExportColumnDef, scale = 1): string {
  if (scale === 1) return col.pdfWidth;
  const pct = parseFloat(col.pdfWidth);
  return `${(pct * scale).toFixed(1)}%`;
}

interface Props {
  payload: ExportPayload;
  perEmployeePage?: boolean;
}

/** Shared: render one employee's data rows (group header + table header + reports + allowances) */
function EmployeeGroup({
  row,
  payload,
  formatNum,
  cols,
  colScale = 1,
}: {
  row: ExportEmployeeRow;
  payload: ExportPayload;
  formatNum: (n: number) => string;
  cols: ExportColumnDef[];
  colScale?: number;
}) {
  return (
    <View wrap={false}>
      {/* Group header */}
      <View style={s.groupHeader}>
        <Text style={s.groupName}>
          {row.employeeName}
          {row.employmentRole ? ` - ${row.employmentRole}` : ''}
          {payload.aggregated ? ` (${row.totalQuantity})` : ` (${row.totalReports})`}
        </Text>
        <Text style={s.groupTotal}>
          {formatNum(row.totalAmount)} {payload.currencySymbol}
        </Text>
      </View>

      {/* Table header */}
      <View style={s.tableHeader}>
        {cols.map((col) => (
          <Text key={col.id} style={[s.th, { width: getColWidth(col, colScale) }]}>
            {col.header}
          </Text>
        ))}
      </View>

      {/* Report rows */}
      {row.reports.map((r, j) => {
        const isRejected = r.status === 'REJECTED';
        const textStyle = isRejected ? s.tdStrike : s.td;
        return (
          <View key={j} style={[s.tableRow, j % 2 === 1 ? s.tableRowAlt : {}]}>
            {cols.map((col) => {
              const isAmount = col.id === 'amount';
              const cellStyle = isAmount
                ? (isRejected ? s.tdStrike : s.tdBold)
                : textStyle;
              const val = isAmount
                ? `${formatNum(r.amount)} ${payload.currencySymbol}`
                : String(col.getValue(r));
              return (
                <Text key={col.id} style={[cellStyle, { width: getColWidth(col, colScale) }]}>
                  {val}
                </Text>
              );
            })}
          </View>
        );
      })}

      {/* Allowance rows */}
      {row.allowances.map((a, j) => (
        <View key={`a-${j}`} style={s.allowanceRow}>
          {cols.map((col) => {
            let val = '';
            switch (col.id) {
              case 'date': val = 'Allowance'; break;
              case 'reportType': val = a.name; break;
              case 'hospital': val = a.period; break;
              case 'quantity': val = '1'; break;
              case 'ratePerUnit': val = String(a.amount); break;
              case 'amount': val = `${formatNum(a.amount)} ${payload.currencySymbol}`; break;
            }
            return (
              <Text key={col.id} style={[s.allowanceText, { width: getColWidth(col, colScale) }]}>
                {val}
              </Text>
            );
          })}
        </View>
      ))}

      {/* Supervision bonus rows */}
      {row.supervisionBonuses.map((sv, j) => (
        <View key={`sv-${j}`} style={s.supervisionRow}>
          {cols.map((col) => {
            let val = '';
            switch (col.id) {
              case 'date': val = 'Supervision Bonus'; break;
              case 'hospital': val = sv.hospitalName; break;
              case 'quantity': val = String(sv.totalUnits); break;
              case 'ratePerUnit': val = String(sv.bonusPerUnit); break;
              case 'amount': val = `${formatNum(sv.amount)} ${payload.currencySymbol}`; break;
            }
            return (
              <Text key={col.id} style={[s.supervisionText, { width: getColWidth(col, colScale) }]}>
                {val}
              </Text>
            );
          })}
        </View>
      ))}

      {/* Employee total */}
      <View style={[s.grandTotals, { marginTop: 4, padding: 6 }]}>
        <Text style={[s.grandLabel, { fontSize: 9 }]}>
          {payload.aggregated
            ? `Total: ${row.totalQuantity} treatments`
            : `Total: ${row.totalReports} reports`}
        </Text>
        <Text style={[s.grandValue, { fontSize: 10 }]}>
          {formatNum(row.totalAmount)} {payload.currencySymbol}
        </Text>
      </View>
    </View>
  );
}

/** Fixed header (logo + divider) shown on every page */
function PageHeader({ payload }: { payload: ExportPayload }) {
  return (
    <>
      <View style={s.logoHeader} fixed>
        <Image src="/images/BrainsWayLogo.png" style={s.logo} />
        <View style={s.logoSpacer} />
        <Text style={s.logoDate}>{payload.generatedAt}</Text>
      </View>
      <View style={s.brandDivider} fixed />
    </>
  );
}

/** Fixed footer shown on every page */
function PageFooter() {
  return (
    <View style={s.footer} fixed>
      <Text style={s.footerText}>BrainsWay Ltd. | Confidential Financial Document</Text>
      <Text
        style={s.footerPage}
        render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
      />
    </View>
  );
}

/** Filters row if any active */
function FiltersRow({ payload }: { payload: ExportPayload }) {
  if (!payload.filters.status && !payload.filters.hospitalName && !payload.filters.roleName) {
    return null;
  }
  return (
    <View style={s.filtersRow}>
      {payload.filters.status && <Text style={s.filterChip}>{payload.filters.status}</Text>}
      {payload.filters.hospitalName && <Text style={s.filterChip}>{payload.filters.hospitalName}</Text>}
      {payload.filters.roleName && <Text style={s.filterChip}>{payload.filters.roleName}</Text>}
    </View>
  );
}

export function PayrollPdfDocument({ payload, perEmployeePage }: Props) {
  const dir = payload.isRTL ? 'rtl' : 'ltr';
  const formatNum = (n: number) =>
    Math.round(n).toLocaleString(payload.language === 'he' ? 'he-IL' : 'en-US');
  const allCols = getColumnDefs(payload.columns ?? ALL_COLUMN_IDS);
  // Per-employee pages already show employee+role in the header, so drop those columns
  const perEmpCols = allCols.filter((c) => c.id !== 'employee' && c.id !== 'employmentRole');
  // Scale per-employee column widths to fill 100%
  const perEmpTotal = perEmpCols.reduce((sum, c) => sum + parseFloat(c.pdfWidth), 0);
  const perEmpScale = perEmpTotal > 0 ? 100 / perEmpTotal : 1;
  const useLandscape = allCols.length > 7;

  // Per-employee: each employee gets their own page
  if (perEmployeePage) {
    return (
      <Document>
        {payload.rows.map((row, i) => (
          <Page key={i} size="A4" style={[s.page, { direction: dir }]}>
            <PageHeader payload={payload} />

            {/* Title + employee name */}
            <View style={s.header}>
              <Text style={s.title}>{payload.title}</Text>
              <Text style={s.subtitle}>
                {row.employeeName}
                {row.employmentRole ? ` - ${row.employmentRole}` : ''}
              </Text>
              <Text style={{ fontSize: 9, color: '#64748b' }}>{payload.month}</Text>
            </View>

            <FiltersRow payload={payload} />

            <EmployeeGroup row={row} payload={payload} formatNum={formatNum} cols={perEmpCols} colScale={perEmpScale} />

            <PageFooter />
          </Page>
        ))}
      </Document>
    );
  }

  // Combined: all employees in one continuous flow
  return (
    <Document>
      <Page size="A4" orientation={useLandscape ? 'landscape' : 'portrait'} style={[s.page, { direction: dir }]}>
        <PageHeader payload={payload} />

        {/* Header */}
        <View style={s.header}>
          <Text style={s.title}>{payload.title}</Text>
          <Text style={s.subtitle}>{payload.month}</Text>
        </View>

        <FiltersRow payload={payload} />

        {/* Summary totals bar */}
        <View style={s.totalsBar}>
          {payload.aggregated ? (
            <>
              <View style={s.totalItem}>
                <Text style={s.totalLabel}>Treatments</Text>
                <Text style={s.totalValue}>{payload.totals.quantity}</Text>
              </View>
              <View style={s.totalItem}>
                <Text style={s.totalLabel}>Total</Text>
                <Text style={s.totalValue}>
                  {formatNum(payload.totals.totalAmount)} {payload.currencySymbol}
                </Text>
              </View>
            </>
          ) : (
            <>
              <View style={s.totalItem}>
                <Text style={s.totalLabel}>Employees</Text>
                <Text style={s.totalValue}>{payload.totals.employeeCount}</Text>
              </View>
              <View style={s.totalItem}>
                <Text style={s.totalLabel}>Reports</Text>
                <Text style={s.totalValue}>{payload.totals.reportCount}</Text>
              </View>
              <View style={s.totalItem}>
                <Text style={s.totalLabel}>Quantity</Text>
                <Text style={s.totalValue}>{payload.totals.quantity}</Text>
              </View>
              <View style={s.totalItem}>
                <Text style={s.totalLabel}>Total</Text>
                <Text style={s.totalValue}>
                  {formatNum(payload.totals.totalAmount)} {payload.currencySymbol}
                </Text>
              </View>
            </>
          )}
        </View>

        {/* Per-therapist groups */}
        {payload.rows.map((row, i) => (
          <EmployeeGroup key={i} row={row} payload={payload} formatNum={formatNum} cols={allCols} />
        ))}

        {/* Grand totals bar */}
        <View style={s.grandTotals}>
          <Text style={s.grandLabel}>
            {payload.aggregated
              ? `Total (${payload.totals.employeeCount} hospitals, ${payload.totals.quantity} treatments)`
              : `Total (${payload.totals.employeeCount} employees, ${payload.totals.reportCount} reports)`}
          </Text>
          <Text style={s.grandValue}>
            {formatNum(payload.totals.totalAmount)} {payload.currencySymbol}
          </Text>
        </View>

        <PageFooter />
      </Page>
    </Document>
  );
}

/** Render payload to PDF Blob — keeps @react-pdf/renderer as a static import in this file */
export async function renderPdfBlob(
  payload: ExportPayload,
  perEmployeePage = false,
): Promise<Blob> {
  const doc = createElement(PayrollPdfDocument, { payload, perEmployeePage });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return pdf(doc as any).toBlob();
}
